import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../../common/utils/froala.utils';
import { DomainValue } from '../../../models/domain.model';
import { LoaderService } from '../../../common/services/loader.service';
import { ReportSelectorService } from '../../../form-controls/services/report-selector.service';
import { SignerType } from '../../models/contact.model';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { lettercontentmodel} from '../../../models/comodel/lettercontentmodel.model';
import { TextTemplatesService } from '../../../text-templates/text-templates.service';
import { ChiefOrderPostIssueOptions, LetterMergeConstants, ChiefsOrderMergeConstants, IReportContextValue, ReportTemplate, ReportTemplateChiefOrderPostIssueOptions } from '../../../models/comodel/mergefieldsmodel.model';
import { OnBaseDocumentGroup, OnBaseDocumentType, OnBaseMappedKeyword, OnBaseProgramArea } from '../../../common/models/onBaseIntegrationAndMapping.model';
import { DocumentManagementService } from '../../../common/services/document-management.service';
import { OnBaseIntegrationAndMappingService } from '../../../common/services/onbase-integration-mapping.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { LunaClaimValues } from '../../../models/user-identity.model';
import { AuthService } from '../../../services/auth.service';
import { RemindersService } from '../../services/reminders.service';

@Component({
    selector: 'communication-template-editor',
    templateUrl: './communication-template-editor.component.html',
    styleUrls: ['./communication-template-editor.component.scss'],
    standalone: false
})
export class CommunicationTemplateEditorComponent implements OnInit, OnDestroy {

    @Input() chiefsOrderCategories: DomainValue[]
    @Input() letterCategories: DomainValue[]
    @Input() reminderTypes: DomainValue[]
    @Input() template: ReportTemplate;
    @Input() allTemplates: ReportTemplate[];
    @Input() types: DomainValue[];
    @Input() onBaseMappingService: DocumentManagementService

    @Output() closed: EventEmitter<any> = new EventEmitter<any>();
    @Output() templateSaved: EventEmitter<ReportTemplate> = new EventEmitter<ReportTemplate>();

    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);

    selectedDocumentGroup: OnBaseDocumentGroup = { id: -1, name: "-- Select a Document Group --" };
    selectedDocumentType: OnBaseDocumentType = { id: -1, name: "-- Select a Document Type --" };
    documentGroups: OnBaseDocumentGroup[] = [{ id: -1, name: "-- Select a Document Group --" }];
    documentTypes: OnBaseDocumentType[] = [{ id: -1, name: "-- Select a Document Type --" }];
    programAreas: OnBaseProgramArea[] = [];
    mappedKeyWords: OnBaseMappedKeyword[] = [];
    public docGroupToTypeDictionary: Map<string, OnBaseDocumentType[]> = new Map<string, OnBaseDocumentType[]>();
    public docGroupToMappedKeywordDictionary: Map<string, OnBaseMappedKeyword[]> = new Map<string, OnBaseMappedKeyword[]>();

    loadingDocTypes = false;
    loadingKeywordMappings = false;
    loadingReminderTypes = false;

    modifyReport: string;
    vacateReport: string;
    suspendReport: string;
    revokeReport: string;
    terminateReport: string;

    subscription: Subscription = new Subscription();

    chiefsOrderReportTemplates: ReportTemplate[];
    letterReportTemplates: ReportTemplate[];

    cats: DomainValue[];
    communicationTypes = CommunicationType;
    signerTypes = SignerType;
    postIssueOptions = ChiefOrderPostIssueOptions;
    letterContentModel = <lettercontentmodel>{
        usePageNumberHeader: true,
        htmlHeader: 'Type header text here',
        htmlContent: 'Type text here',
        htmlFooter: 'Type footer text here',
        id: ''
    }

    public orgAddressPair = { displayName: 'Organization Address', contextValue: 'org.address.street' } as IReportContextValue;
    public orgCityPair = { displayName: 'Organization City', contextValue: 'org.address.city' } as IReportContextValue;
    public orgIdPair = { displayName: 'Owner Number', contextValue: 'org.id' } as IReportContextValue;
    public orgNamePair = { displayName: 'Organization Name', contextValue: 'org.name' } as IReportContextValue;
    public orgStatePair = { displayName: 'Organization State', contextValue: 'org.address.stateCode' } as IReportContextValue;
    public orgZipPair = { displayName: 'Organization Zip', contextValue: 'org.address.zipCode' } as IReportContextValue;
    public orgPairs = [this.orgIdPair, this.orgNamePair, this.orgAddressPair, this.orgCityPair, this.orgStatePair, this.orgZipPair];
    public showHideLetterEditor: boolean = false;    

    private _froalaOptions: any = null;
    get froalaOptions(): Object {
        if (!this._froalaOptions) {
            let conf = window["PdfConfig"] as any;
            let key = (conf != null) ? conf.editorLicenseKey : "";
            this._froalaOptions = applyFroalaAriaFix({
                heightMax: 500,
                height: 500,
                heightMin: 150,
                key: key,
                fontSize: ['8', '9', '10', '11', '12', '14', '16', '18', '20', '22', '24', '26', '26.5', '27', '30', '60', '72', '96'],
                fontFamily: {
                    'Arial,Helvetica,sans-serif': 'Arial',
                    'Georgia,serif': 'Georgia',
                    'Impact,Charcoal,sans-serif': 'Impact',
                    'Tahoma,Geneva,sans-serif': 'Tahoma',
                    "'Times New Roman',Times,serif": 'Times New Roman',
                    'Verdana,Geneva,sans-serif': 'Verdana',
                    'Gabriola,sans-serif': 'Gabriola'
                },
                toolbarButtons: ["fullscreen", "html", "|", "undo", "redo", "|", "bold", "italic", "underline", 'strikeThrough', 'strikeThrough', "|", "formatOL", "formatUL", "|", "align", "fontFamily", "fontSize", "paragraphFormat", "insertLink", "|", "outdent", "indent", "clearFormatting", "insertTable", "color"]
            });
        }
        return this._froalaOptions;
    }
    
    constructor(public loader: LoaderService, public _mappingService: OnBaseIntegrationAndMappingService, public authService: AuthService, 
        public reportService: ReportSelectorService, public textService: TextTemplatesService, public reminderService: RemindersService,
        private _dialogService: DynamicDialogService) { }

    ngOnInit() {

        if (this.template) {

            if (!this.template.allowedSignerTypes)
                this.template.allowedSignerTypes = [];

            if (!this.template.reportContextValues)
                this.template.reportContextValues = [];

            if (!this.template.chiefsOrderPostIssueOptions)
                this.template.chiefsOrderPostIssueOptions = [];


            let allConstants: IReportContextValue[] = [];

            if (this.template.typeCode.toLowerCase() == 'co') {
                allConstants = ChiefsOrderMergeConstants.AllConstants;
                this.buildMergeFields(allConstants);
            }

            if (this.template.typeCode.toLowerCase() == 'letter') {
                allConstants = LetterMergeConstants.AllConstants;
                this.buildMergeFields(allConstants);
            }

            if (this.template.typeCode.toLowerCase() == 'reminder' && this.template.categoryCode != null && this.template.categoryCode != undefined) {
                this.fetchReminderCategoryMergeFields(this.template.categoryCode)
            }

            this.setCategories(this.template.categoryCode);

        }
        else
            this.setCategories();

        let documentGroupsObs$ = this._mappingService.getAllDocumentGroups().pipe(tap((documentGroups: OnBaseDocumentGroup[]) => {
            this.documentGroups = this.documentGroups.concat(documentGroups);
            if (this.template && this.template.onBaseDocumentGroupName) {
                this.selectedDocumentGroup = this.documentGroups.filter(a => a.name == this.template.onBaseDocumentGroupName)[0];
            }
        })).subscribe(() => { },
            (error) => {
                console.error(error);
                this.handleError(error, "An error occurred when trying to fetch the available Document Groups from OnBase.");
            }
        );
        this.subscription.add(documentGroupsObs$);

        if (this.template && this.template.onBaseDocumentGroupName) {
            let documentGroupTypesObs$ = this._mappingService.getDocumentTypesByDocumentGroup(this.template.onBaseDocumentGroupName).pipe(tap((documentTypes: OnBaseDocumentType[]) => {
                documentTypes.unshift({ id: -1, name: "-- Select a Document Type --" })
                this.docGroupToTypeDictionary.set(this.selectedDocumentGroup.name, documentTypes);
                this.loadingDocTypes = false;
                if (this.template.onBaseDocumentTypeName) {
                    this.selectedDocumentType = documentTypes.filter(a => a.name == this.template.onBaseDocumentTypeName)[0];
                }
            })).subscribe(() => { },
                (error) => {
                    console.error(error);
                    this.handleError(error, "An error occurred when trying to fetch the related Document Types.");
                    this.loadingDocTypes = false;
                }
            );
            this.subscription.add(documentGroupTypesObs$);

            let documentTypeKeywordsObs$ = this._mappingService.GetKeywordsByDocumentGroup(this.template.onBaseDocumentGroupName)
                .pipe(tap((mappedKeywords: OnBaseMappedKeyword[]) => {
                    //Strip out the file name one, as it will be set per file as they are uploaded within the DocumentManagementLogic Repository's CommitDocument method
                    this.mappedKeyWords = mappedKeywords.filter(a => a.name != "DOGRM - Legacy File Name");
                    this.docGroupToMappedKeywordDictionary.set(this.selectedDocumentType.name, mappedKeywords);
                    this.loadingKeywordMappings = false;
                })).subscribe(() => { },
                    (error) => {
                        console.error(error);
                        this.handleError(error, "An error occurred when trying to fetch the related Keywords.");
                        this.loadingKeywordMappings = false;
                    }
                );
            this.subscription.add(documentTypeKeywordsObs$);
        }

        this.chiefsOrderReportTemplates = this.allTemplates.filter(a => a.typeCode.toLowerCase() === CommunicationType.ChiefOrder);
        this.letterReportTemplates = this.allTemplates.filter(a => a.typeCode.toLowerCase() === CommunicationType.Letter);

        if (this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Modify).length > 0) {
            this.modifyReport = this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Modify)[0].associatedReportTemplateId;
        }
        if (this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Vacate).length > 0) {
            this.vacateReport = this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Vacate)[0].associatedReportTemplateId;
        }
        if (this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Suspend).length > 0) {
            this.suspendReport = this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Suspend)[0].associatedReportTemplateId;
        }
        if (this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Revoke).length > 0) {
            this.revokeReport = this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Revoke)[0].associatedReportTemplateId;
        }
        if (this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Terminate).length > 0) {
            this.terminateReport = this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == ChiefOrderPostIssueOptions.Terminate)[0].associatedReportTemplateId;
        }
    }

    ngOnDestroy(){
        if (this.subscription) this.subscription.unsubscribe();
    }

    sortReportContextValues(a: IReportContextValue, b: IReportContextValue): number {
        if (!a.systemProvided && b.systemProvided) return -1;
        if (a.systemProvided && !b.systemProvided) return 1;

        if (a.displayName < b.displayName) return -1;
        if (a.displayName > b.displayName) return 1;

        return 0;
    }


    addOrgFields(): void {
        this.orgPairs.forEach(pair => {
            if (!this.template.reportContextValues.find(x => x.replaceValue == pair.replaceValue)) {
                this.template.reportContextValues.push(pair);
            }
        });
    }

    addValue(): void {
        //find index of first system item
        var indexOfFirstSystemReportContextValue = this.template.reportContextValues.findIndex(a => a.systemProvided === true);

        let newValue = {
            displayName: '',
            contextValue: '',
            replaceValue: '',
            onBaseKeywordName: '',
            systemProvided: false
        } as IReportContextValue;
        this.template.reportContextValues.splice(indexOfFirstSystemReportContextValue, 0, newValue);
          
    }

    catChanged(code: string): void {
        this.template.categoryCode = code;
        if (this.type == CommunicationType.Reminder)
            this.fetchReminderCategoryMergeFields(code);
    }

    onBaseDocumentTypeChanged(onBaseDocumentTypeName: string): void {
        this.template.onBaseDocumentTypeName = onBaseDocumentTypeName;
    }

    close(): void {
        this.closed.emit(null);
    }
    hide(): void {
        this.showHideLetterEditor = false;
    }

    showLetterEditor(): void {
         
        this.showHideLetterEditor = true;
        this.loadContent();      
      
    }

    loadContent(): void {
        this.loader.toggleLoader(true);
        var sub3$ = this.textService.getTemplateContent(this.template.id)
            .pipe(
                finalize(() => {
                    this.subscription.add(sub3$);
                    this.loader.toggleLoader(false);                   
                })
            )
            .subscribe(res => {
                this.letterContentModel.usePageNumberHeader = res.usePageNumberHeader;
                this.letterContentModel.htmlHeader = res.htmlHeader;
                if (!this.isntNullOrEmpty(this.letterContentModel.htmlHeader) && this.template.typeCode.toLowerCase() == 'co') {
                    this.letterContentModel.htmlHeader = "<table style=\"width: 100%;\"<tr><td style=\"width: 50%; text-align: left;\">[organizationName]</td><td style=\"width: 50%; text-align: right;\">[chiefOrderNumber]<td></tr></table>";
                }
                this.letterContentModel.htmlContent = res.htmlContent;
                this.letterContentModel.htmlFooter = res.htmlFooter;
                this.letterContentModel.id = res.id;
            },
                err => { console.log(err); });
    }

    saveContent(): void {
        this.loader.toggleLoader(true);
        this.letterContentModel.id = this.template.id;

        this.letterContentModel.htmlHeader = stripFroalaWatermark(this.letterContentModel.htmlHeader);
        this.letterContentModel.htmlContent = stripFroalaWatermark(this.letterContentModel.htmlContent);
        this.letterContentModel.htmlFooter = stripFroalaWatermark(this.letterContentModel.htmlFooter);
        let sub2$ = this.textService.saveTemplateContent(this.letterContentModel)
            .pipe(
                finalize(() => {
                    this.subscription.add(sub2$);
                    this.loader.toggleLoader(false); 
                })
            )
            .subscribe(res => {
                this.letterContentModel = res;
                this.loader.toggleLoader(false);
            },
                err => { console.log(err); }); 
    }

    movePair(direction: string, currentPosition: number): void {
        if (currentPosition === 0 && direction == 'up') return; // Guard lower bound
        if (currentPosition === (this.template.reportContextValues.length - 1) && direction == 'down') return; // Guard upper bound

        const positionToMoveTo = (direction == 'up') ? currentPosition - 1 : currentPosition + 1
        const temp = this.template.reportContextValues[positionToMoveTo];
        this.template.reportContextValues[positionToMoveTo] = this.template.reportContextValues[currentPosition];
        this.template.reportContextValues[currentPosition] = temp;
    }

    removeValue(i: number): void {
        this.template.reportContextValues.splice(i, 1);
    }

    isntNullOrEmpty(stringToTest: string) {
        return (stringToTest != null && stringToTest != "");
    }

    save(): void {
        this.loader.toggleLoader(true);
        var sub1$ = this.reportService.saveTemplate(this.template)
            .pipe(
                finalize(()=>{
                    this.subscription.add(sub1$);
                    this.loader.toggleLoader(false); 
                })
            )
            .subscribe(res => {
                this.templateSaved.emit(this.template);
            },
            err => { console.log(err); }); 
    }

    setCategories(codeToSet: string = null): void {
        if (this.type == CommunicationType.ChiefOrder) this.cats = this.chiefsOrderCategories;
        if (this.type == CommunicationType.Letter) this.cats = this.letterCategories;
        if (this.type == CommunicationType.Reminder) this.cats = this.reminderTypes;

        if (!this.isntNullOrEmpty(codeToSet))
            codeToSet = this.cats[0].Code;

        this.catChanged(codeToSet);
    }

    signerTypeClick(selected: boolean, signerType: SignerType) {
        if (!this.template.allowedSignerTypes) this.template.allowedSignerTypes = [];
        if (selected)
            if (!this.template.allowedSignerTypes.find(x => x == signerType))
                this.template.allowedSignerTypes.push(signerType);

        if (!selected) {
            const index = this.template.allowedSignerTypes.findIndex(x => x == signerType);
            if (index !== -1) this.template.allowedSignerTypes.splice(index, 1);
        }
    }

    get type(): CommunicationType {
        if (this.template.typeCode.toLowerCase() === 'co') return CommunicationType.ChiefOrder;
        if (this.template.typeCode.toLowerCase() === 'letter') return CommunicationType.Letter;
        if (this.template.typeCode.toLowerCase() === 'reminder') return CommunicationType.Reminder;
    }

	typeChanged(code: string): void {
        this.template.typeCode = code;
        this.setCategories();
		this.template.categoryCode = this.cats[0].Code;
		if (code.toLocaleLowerCase() === CommunicationType.ChiefOrder) {
			this.buildMergeFields(ChiefsOrderMergeConstants.AllConstants);
		} else if (code.toLocaleLowerCase() === CommunicationType.Letter) {
			this.buildMergeFields(LetterMergeConstants.AllConstants);
		}
    }

    docGroupSelected(event: any) {
        this.selectedDocumentGroup = event;
        this.selectedDocumentType = { id: -1, name: "-- Select a Document Type --" };
        this.documentTypes = [{ id: -1, name: "-- Select a Document Type --" }];
        this.template.onBaseDocumentTypeName = "-- Select a Document Type --";

        if (this.selectedDocumentGroup && this.selectedDocumentGroup.name != "-- Select a Document Group --" && !this.docGroupToTypeDictionary.has(this.selectedDocumentGroup.name)) {
            this.loadingDocTypes = true;
            let documentGroupTypesObs$ = this._mappingService.getDocumentTypesByDocumentGroup(this.selectedDocumentGroup.name).pipe(tap((documentTypes: OnBaseDocumentType[]) => {
                documentTypes.unshift({ id: -1, name: "-- Select a Document Type --" })
                this.docGroupToTypeDictionary.set(this.selectedDocumentGroup.name, documentTypes);
                this.loadingDocTypes = false;
            })).subscribe(() => { },
                (error) => {
                    console.error(error);
                    this.handleError(error, "An error occurred when trying to fetch the related Document Types.");
                    this.loadingDocTypes = false;
                }
            );
            this.subscription.add(documentGroupTypesObs$);
        }
    }

    docTypeSelected(event: any) {
        this.selectedDocumentType = event;
        if (this.selectedDocumentType && this.selectedDocumentType.name != "-- Select a Document Type --" && !this.docGroupToMappedKeywordDictionary.has(this.selectedDocumentGroup.name)) {
            this.loadingKeywordMappings = true;

            let documentTypeKeywordsObs$ = this._mappingService.GetKeywordsByDocumentGroup(this.selectedDocumentGroup.name)
                .pipe(tap((mappedKeywords: OnBaseMappedKeyword[]) => {
                    //Strip out the file name one, as it will be set per file as they are uploaded within the DocumentManagementLogic Repository's CommitDocument method
                    this.mappedKeyWords = mappedKeywords.filter(a => a.name != "DOGRM - Legacy File Name");
                    this.docGroupToMappedKeywordDictionary.set(this.selectedDocumentType.name, mappedKeywords);
                    this.loadingKeywordMappings = false;
                })).subscribe(() => { },
                    (error) => {
                        console.error(error);
                        this.handleError(error, "An error occurred when trying to fetch the related Keywords.");
                        this.loadingKeywordMappings = false;
                    }
                );
            this.subscription.add(documentTypeKeywordsObs$);
        }
        else if (this.selectedDocumentType && this.docGroupToMappedKeywordDictionary.has(this.selectedDocumentType.name)) {
            this.mappedKeyWords = this.docGroupToMappedKeywordDictionary.get(this.selectedDocumentType.name);
        }
    }

    filteredDocTypes() {
        //If a document group has been selected, filter down the available doc types
        if (this.selectedDocumentGroup && this.docGroupToTypeDictionary.has(this.selectedDocumentGroup.name)) {
            this.documentTypes = this.docGroupToTypeDictionary.get(this.selectedDocumentGroup.name);
        }

        return this.documentTypes;
    }

    validDocumentTypeSelected(): boolean {
        return this.selectedDocumentGroup && this.selectedDocumentGroup.name != "-- Select a Document Group --"
            && this.selectedDocumentType && this.selectedDocumentType.name != "-- Select a Document Type --";
    }

    handleError(error: any, message: string): void {
        this.errorDialog.toggleDialog(true);
        this.errorDialog.message = message;
        this._dialogService.updateDialog(this.errorDialog);
    }

    isPostIssueOptionEnabled(option: ChiefOrderPostIssueOptions): boolean {
        if (this.template.chiefsOrderPostIssueOptions) {
            return this.template.chiefsOrderPostIssueOptions.filter(a => a.typeOfPostIssueOption == option).length > 0;
        }
        else return false;
    }

    postIssueOptionClick(selected: boolean, option: ChiefOrderPostIssueOptions) {
        if (!this.template.chiefsOrderPostIssueOptions) this.template.chiefsOrderPostIssueOptions = [];
        if (selected)
            if (!this.template.chiefsOrderPostIssueOptions.find(x => x.typeOfPostIssueOption == option))
                this.template.chiefsOrderPostIssueOptions.push({ typeOfPostIssueOption: option, associatedReportTemplateId: "" } as ReportTemplateChiefOrderPostIssueOptions);

        if (!selected) {
            const index = this.template.chiefsOrderPostIssueOptions.findIndex(x => x.typeOfPostIssueOption == option);
            if (index !== -1) this.template.chiefsOrderPostIssueOptions.splice(index, 1);
        }
    }

    postIssueStepTemplateSelected(selectedReportTemplate: any, option: ChiefOrderPostIssueOptions) { 
        var relevantPostIssueStepTemplate = this.template.chiefsOrderPostIssueOptions.find(x => x.typeOfPostIssueOption == option);
        relevantPostIssueStepTemplate.associatedReportTemplateId = selectedReportTemplate.id;
    }

    get canManageComunnications(): boolean {
        if (this.authService) {
            let hasPermissions = this.authService.hasSystemPermission(LunaClaimValues.ManageCommunications) !== undefined;
            return hasPermissions;
        }
        return false;
    }

    fetchReminderCategoryMergeFields(code: string) {
        if (!this.loadingReminderTypes) {
            this.loadingReminderTypes = true;
            this.loader.toggleLoader(true);
            let numberConvertedEnumValue = Number(this.template.categoryCode);
            let reminderMergeFieldsObs$ = this.reminderService.getReminderTypeMergeFields(numberConvertedEnumValue).pipe(tap((reminderMergeFields: IReportContextValue[]) => {
                let allConstants = reminderMergeFields;
                this.buildMergeFields(allConstants);
            })).subscribe(() => {
                this.loader.toggleLoader(false);
                this.loadingReminderTypes = false;
            },
                (error) => {
                    this.loader.toggleLoader(false);
                    this.loadingReminderTypes = false;
                    console.error(error);
                    this.handleError(error, "An error occurred when trying to fetch mergefield for this reminder type.");
                }
            );
            this.subscription.add(reminderMergeFieldsObs$);
        }
    }

    buildMergeFields(allConstants: IReportContextValue[]) {
        //initiate the list of merge fields
        let existingEntries: IReportContextValue[] = [];      

        //run it against the current list of Constants, and remove any that no longer apply           
        this.template.reportContextValues.forEach(x => {
            if (x.systemProvided) {
                let matchingConstants = allConstants.filter(a => (a.displayName == x.displayName && this.isntNullOrEmpty(x.displayName))
                    || (a.contextValue == x.contextValue && this.isntNullOrEmpty(x.contextValue)));

                //if a field tagged as system provided matches any on the current list of system provided fields, add it, else do nothing so it doesn't make it into next save
                if (matchingConstants.length > 0) {
                    existingEntries.push(x);
                }
            }
            else {
                //If an entry is present that's not system provided, keep it unless the current type is reminder, which doesn't allow for custom merge fields
                if (this.template.typeCode.toLowerCase() != 'reminder')
                    existingEntries.push(x);
            }
        });

        //Now add back in any of the new constants not yet present
        allConstants.forEach(x => {
            //If an entry without any of the set merge field values exist, add it back
            if (existingEntries.filter(a => (a.displayName == x.displayName && this.isntNullOrEmpty(x.displayName))
                || (a.contextValue == x.contextValue && this.isntNullOrEmpty(x.contextValue))).length == 0)
                existingEntries.push(x);
        });

        this.template.reportContextValues = existingEntries.sort((a, b) => this.sortReportContextValues(a, b));
    }

    togglePageNumberHeader() {
        this.letterContentModel.usePageNumberHeader = !this.letterContentModel.usePageNumberHeader;
    }
}

export enum CommunicationType {
    ChiefOrder = 'co',
    Letter = 'letter',
    Reminder = 'reminder'
}